import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { ScreenType } from 'src/app/enums/ScreenType';
import { UserRegDetails } from 'src/app/models/user';
import { IUserPolls } from 'src/app/models/user-polls';

interface QuickDateOption {
  label: string;
  days: number;
}

interface CreatePostData {
  screenSize: ScreenType;
  users: UserRegDetails[];
  pollData?: IUserPolls;
  loggedIsUsernameProfile: string;
  loggedIsUsername: string;
}

@Component({
  selector: 'app-create-notice-board-posts',
  templateUrl: './create-notice-board-posts.component.html',
  styleUrls: ['./create-notice-board-posts.component.scss'],
})
export class CreateNoticeBoardPostsComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('postTextarea') postTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('userSuggestions') userSuggestions!: ElementRef<HTMLDivElement>;
  @ViewChild('datePicker') datePicker!: ElementRef<HTMLDivElement>;

  @Output() onCreateUserPost = new EventEmitter<any>();

  // Component properties
  screenInfo: ScreenType = ScreenType.Desktop;
  ScreenType = ScreenType;

  // User data
  loggedIsUsernameProfile = '';
  loggedIsUsername = '';
  users: UserRegDetails[] = [];
  filteredUserOrgList: UserRegDetails[] = [];
  postMentionedUsers: UserRegDetails[] = [];

  // Post content
  postText = '';
  uploadedAttachment = '';
  selectedDate: Date | null = null;
  selectedDateString = '';

  // UI states
  showUserList = false;
  showDatePicker = false;
  selectedIndex = -1;
  isPosting = false;
  isPollShared = false;
  pollTitle = '';
  pollData: IUserPolls | null = null;

  // Date options
  minDate: Date = new Date();
  maxDate: Date = new Date();
  minDateString = '';
  maxDateString = '';

  quickDateOptions: QuickDateOption[] = [
    { label: '1 Day', days: 1 },
    { label: '3 Days', days: 3 },
    { label: '1 Week', days: 7 },
    { label: '2 Weeks', days: 14 },
  ];

  // Default user image
  defaultUserImage =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAMAAAD04JH5AAAAMFBMVEXk5ueutLepr7Ln6erg4uPJzc/T1ti7wMO2u77DyMqzuLvq7O3P0tTW2dvc3+DZ3N7ueM45AAAC2UlEQVR4nO2a2ZLjIAxFAYHZvPz/3za2O8v0JCA5kl3dxXly5eXeCAmDZKU6nU6n0+l0Op1O5/cCAErZglofzle3UwxJryTvFnWuB4DJa2P0DWNSnM+zAHnUD/G7Cb/kc/Tz9EJ+t3DKQkB8Lb8xiDsAFSr62jhhB2BTRX51EEUdgK3Lb4kg6qDx/6VjAL6tXxyMUg7A1fLvyYHQngQLTr/szUIGAlJfqBhhxAagIKCvAC+vtUQIBkIAtOE3kNEZsBkQKEVKALQO3AZIKbiGwHIbQG2CTwbYX8w0fa253wgzbQVKEvDqA6kIN5gNIN9DT8y8BiJVXy8XGzATrwFiFa51+McMXL4Elyfh1WVIOw1s8OorS96K/9rL6PLXMfVAorkPJApoBvivqLQ1MBP/oRR9MdsQuBcA4mp+D4DI3WwihEDkdorPAv4j8U67P/ONVJcGvRdYqRZJ9hgHEiV4A9OjkGsRbVTblJu+dKuy4UBcvxRjtVUr3ywup7P38uGUoQHM74Lgzprd5OVFJpgoVv6vLMzu35FNGE+eGq1TqzH6kFIKPg7XDM4y3Mhnz8zK/7XLMLq44cZhmfdfTxDPah5jKKu+ck+B9TlFNynRYKzDSheek+9nIWiT4mBlIlGCPnj9ZmD3bMLo4Gb2kthHpS3xhwk/BBBF+CCAC+MlAaCBB';

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CreateNoticeBoardPostsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreatePostData
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.setupDateLimits();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    if (this.data) {
      this.screenInfo = this.data.screenSize;
      this.users = this.data.users || [];
      this.loggedIsUsernameProfile = this.data.loggedIsUsernameProfile;
      this.loggedIsUsername = this.data.loggedIsUsername;
      this.pollData = this.data.pollData || null;

      if (this.pollData) {
        this.isPollShared = true;
        this.pollTitle = this.pollData.pollTitle || '';
      }
    }
  }

  private setupDateLimits(): void {
    const today = new Date();
    this.minDate = new Date(today);

    // Set max date to 1 year from now
    this.maxDate = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate()
    );

    this.minDateString = this.formatDateForInput(this.minDate);
    this.maxDateString = this.formatDateForInput(this.maxDate);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Close user list if clicked outside
    if (
      this.showUserList &&
      !this.postTextarea?.nativeElement.contains(target) &&
      !this.userSuggestions?.nativeElement.contains(target)
    ) {
      this.hideUserList();
    }

    // Close date picker if clicked outside
    if (
      this.showDatePicker &&
      !this.datePicker?.nativeElement.contains(target)
    ) {
      this.hideDatePicker();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.showUserList) {
      this.handleUserListNavigation(event);
    }
  }

  private handleUserListNavigation(event: KeyboardEvent): void {
    const filteredCount = this.filteredUserOrgList.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = (this.selectedIndex + 1) % filteredCount;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex =
          this.selectedIndex > 0 ? this.selectedIndex - 1 : filteredCount - 1;
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && this.selectedIndex < filteredCount) {
          this.selectUser(this.filteredUserOrgList[this.selectedIndex]);
        }
        break;
      case 'Escape':
        this.hideUserList();
        break;
    }
  }

  onTextChange(): void {
    this.detectMentions();
  }

  private detectMentions(): void {
    const text = this.postText;
    const cursorPosition = this.postTextarea?.nativeElement.selectionStart || 0;
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const searchTerm = textBeforeCursor.substring(lastAtIndex + 1);

      if (searchTerm.length >= 0) {
        this.filterUsers(searchTerm);
        this.showUserList = true;
        this.selectedIndex = 0;
      } else {
        this.hideUserList();
      }
    } else {
      this.hideUserList();
    }
  }

  private filterUsers(searchTerm: string): void {
    const term = searchTerm.toLowerCase();
    this.filteredUserOrgList = this.users
      .filter(
        (user) =>
          user.firstname?.toLowerCase().includes(term) ||
          user.lastname?.toLowerCase().includes(term)
      )
      .slice(0, 5); // Limit to 5 suggestions
  }
  closeDialog(): void {
    this.dialogRef.close();
  }
  isTextareaFocused = false;
  public Math = Math;
  // Get container CSS class based on screen type
  getContainerClass(): string {
    switch (this.screenInfo) {
      case ScreenType.Mobile:
        return 'mobile-container';
      case ScreenType.Tablet:
        return 'tablet-container';
      default:
        return 'desktop-container';
    }
  }
  getDatePickerDarkBgClass(): string {
    return this.showDatePicker ? 'dark:bg-indigo-900/20' : '';
  }
  // Handle text input with mention detection
  onTextInput(event: Event): void {
    this.onTextChange(); // This calls your existing detectMentions logic
  }

  // Select file trigger
  selectFile(): void {
    this.fileInput?.nativeElement.click();
  }

  // Handle file change event
  handleFileChange(event: Event): void {
    this.onFileSelect(event); // This calls your existing file handling logic
  }

  // Remove image method
  removeImage(): void {
    this.removeAttachment(); // This calls your existing removal logic
  }

  // Close date picker
  closeDatePicker(): void {
    this.hideDatePicker(); // This calls your existing hide logic
  }
  // Select quick date with number of days
  selectQuickDate(days: number): void {
    const date = new Date();
    date.setDate(date.getDate() + days);

    this.selectedDate = date;
    this.selectedDateString = this.formatDateForInput(date);
    this.closeDatePicker();
  }

  // Check if post button should be disabled
  isPostDisabled(): boolean {
    return !this.canPost();
  }
  selectUser(user: UserRegDetails): void {
    const text = this.postText;
    const cursorPosition = this.postTextarea?.nativeElement.selectionStart || 0;
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const beforeAt = text.substring(0, lastAtIndex);
      const afterCursor = text.substring(cursorPosition);
      const userName = `@${user.firstname}`;

      this.postText = beforeAt + userName + ' ' + afterCursor;

      // Add user to mentioned users if not already added
      if (!this.postMentionedUsers.find((u) => u.userId === user.userId)) {
        this.postMentionedUsers.push(user);
      }
    }

    this.hideUserList();
    this.focusTextarea();
  }

  private hideUserList(): void {
    this.showUserList = false;
    this.selectedIndex = -1;
    this.filteredUserOrgList = [];
  }

  private focusTextarea(): void {
    setTimeout(() => {
      this.postTextarea?.nativeElement.focus();
    }, 0);
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      // Validate file type and size
      if (this.isValidFile(file)) {
        this.processFile(file);
      } else {
        alert(
          'Please select a valid file (image, video, or document under 10MB)'
        );
        input.value = '';
      }
    }
  }

  private isValidFile(file: File): boolean {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/',
      'video/',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument',
    ];

    return (
      file.size <= maxSize &&
      allowedTypes.some((type) => file.type.startsWith(type))
    );
  }

  private processFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.uploadedAttachment = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeAttachment(): void {
    this.uploadedAttachment = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
  }

  private hideDatePicker(): void {
    this.showDatePicker = false;
  }

  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const dateValue = input.value;

    if (dateValue) {
      this.selectedDate = new Date(dateValue);
      this.selectedDateString = this.formatDisplayDate(this.selectedDate);
    } else {
      this.selectedDate = null;
      this.selectedDateString = '';
    }
  }

  private formatDisplayDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  clearDate(): void {
    this.selectedDate = null;
    this.selectedDateString = '';
  }

  removePoll(): void {
    this.isPollShared = false;
    this.pollData = null;
    this.pollTitle = '';
  }

  getUserDisplayName(user: UserRegDetails): string {
    return (
      `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Unknown User'
    );
  }

  getUserImage(user: UserRegDetails): string {
    return user.profilePicture || this.defaultUserImage;
  }

  canPost(): boolean {
    return (
      (this.postText.trim().length > 0 ||
        this.uploadedAttachment ||
        this.isPollShared) &&
      !this.isPosting
    );
  }

  createPost(): void {
    if (!this.canPost()) return;
    if (!this.selectedDate) {
      this.selectedDate = new Date();
      const numberOfDaysToAdd = 5;
      this.selectedDate.setDate(
        this.selectedDate.getDate() + numberOfDaysToAdd
      );
    }
    this.isPosting = true;
    // const userPost = {
    //       attachment: this.uploadedAttachment,
    //       description: this.createPostControl.value,
    //       username: this.loggedIsUsername,
    //       sharedPoll: this.isPollShared,
    //       postSharedId: this.pollData?.pollId,
    //       validDate: this.selectedDate.toISOString(),
    //       userProfile: localStorage.getItem('ProfilePic')
    //         ? localStorage.getItem('ProfilePic')
    //         : this.userImage,
    //       postMentioned: this.postMentionedUsers,
    //     };

    const postData = {
      attachment: this.uploadedAttachment,
      description: this.postText.trim(),
      postMentioned: this.postMentionedUsers,
      validDate: this.selectedDate.toISOString(),
      pollData: this.isPollShared ? this.pollData : null,
      createdAt: new Date(),
      username: this.loggedIsUsername,
      userProfile: this.loggedIsUsernameProfile,
    };

    // Emit the post data
    this.onCreateUserPost.emit(postData);

    // Close dialog after a short delay
    // setTimeout(() => {
    //   this.dialogRef.close(postData);
    // }, 500);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
